import React, { KeyboardEvent, useEffect, useRef, useState } from "react";
import { IInputRef, Input } from "../input/Input";
import { MyTooltip } from "../tooltip/Tooltip";
import { clx } from "../utils/stringUtils";
import styles from "./CellRenderer.module.scss";
import { ENTER_EDIT_EVENT, IColumn, IRowDataValueChangeProps, TExtend } from "./VGrid";
import { ResetIconThemed } from "@src/assets/general-icons/ResetIconThemed";

export interface ICellRendererProps<T extends TExtend> {
	column: IColumn<T>;
	rowData: T;
	theme: "primary" | "secondary";
	onRowDataValueChange?: (params: IRowDataValueChangeProps<T>) => void;
	rowId: string | number;
	changesMap: Map<string, IRowDataValueChangeProps<T>>;
	isSaving?: boolean;
	disableBorder?: boolean;
	highlightIfOverThreshold?: boolean;
	onAdvance?: (params: { rowId: string | number; columnId: string }) => void;
}

export const CellRenderer = <T extends TExtend>({
	column,
	theme = "primary",
	rowData,
	onRowDataValueChange,
	rowId,
	changesMap,
	disableBorder,
	isSaving = false,
	highlightIfOverThreshold,
	onAdvance,
}: ICellRendererProps<T>) => {
	const inputRef = useRef<IInputRef>(null);
	const tdRef = useRef<HTMLTableCellElement>(null);

	const randomId = btoa(String(Math.random())).slice(0, -2);
	const hasTooltip = column.hasTooltip;
	const changeMapId = `row:${rowId}-col:${column.id}`;
	const value = column.valueGetter?.({ rowData, column }) || (column.accessor ? rowData[column.accessor] : "");

	const initialValue = changesMap.has(changeMapId) ? changesMap.get(changeMapId)?.value : value;
	const [state, setState] = useState<{
		isEdit: boolean;
		originalValue: typeof value;
		value: typeof value;
		editStartValue: typeof value;
	}>({
		isEdit: false,
		originalValue: value,
		value: initialValue,
		editStartValue: initialValue,
	});

	const isModified = state.originalValue != state.value;

	const isOver30PercentDifference = (original: number, updated: number): boolean => {
		if (!original || isNaN(original) || isNaN(updated)) return false;
		const diff = Math.abs(updated - original);
		const percent = diff / original;
		return percent > 0.3;
	};

	const shouldHighlight = highlightIfOverThreshold === true;
	const isOverThreshold = shouldHighlight && isOver30PercentDifference(Number(state.originalValue), Number(state.value));

	const params = { rowData, value, column };
	const displayValue = column.formatValue?.(state.value, params) ?? state.value;
	const title = column.disableTitle ? undefined : (column.formatTooltip?.(state.value, params) ?? displayValue);
	let content = column.cellRenderer?.(params) ?? (
		<div
			title={typeof title == "object" ? "" : title}
			className={clx({
				[styles.content]: true,
				[styles.dirty]: isModified,
				[styles.overThreshold]: isOverThreshold,
				[column.getClassName?.(params) ?? ""]: true,
			})}
			style={{
				width: column.width,
				...(column.fullWidth ? { width: "100%" } : {}),
				...(column.getStyle?.({ column, rowData, value }) || {}),
			}}>
			<div className={styles.value}>{displayValue}</div>
			{isModified && (
				<button
					className={clx({ [styles.resetIcon]: true })}
					onClick={() => {
						if (!column.accessor) return;
						onRowDataValueChange?.({
							accesor: column.accessor,
							value: state.originalValue,
							originalValue: state.originalValue,
							columnId: column.id,
							rowId: rowId,
							rowData: rowData,
						});
						setState((x) => ({ ...x, value: x.originalValue }));
					}}>
					<ResetIconThemed />
				</button>
			)}
		</div>
	);
	const isEditable = typeof column.isEditable == "function" ? column.isEditable(params) : !!column.isEditable;

	if (hasTooltip) {
		content = <MyTooltip place="top-start" id={randomId} element={<>{content}</>} />;
	}

	useEffect(() => {
		if (!state.isEdit || !inputRef.current) return;
		inputRef.current.focus();
		inputRef.current.select();
	}, [state.isEdit]);

	const enterEditMode = () => {
		setState((x) => {
			const formatted =
				column.setTofixed !== undefined && x.value !== "" && !isNaN(Number(x.value))
					? Number(x.value).toFixed(column.setTofixed)
					: x.value;
			return { ...x, isEdit: true, value: formatted, editStartValue: x.value };
		});
	};

	useEffect(() => {
		const el = tdRef.current;
		if (!el || !isEditable) return;
		const handleEnterEdit = () => {
			if (state.isEdit || column.onEditClick) return;
			enterEditMode();
		};
		el.addEventListener(ENTER_EDIT_EVENT, handleEnterEdit);
		return () => el.removeEventListener(ENTER_EDIT_EVENT, handleEnterEdit);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isEditable, state.isEdit]);

	const handleSave = () => {
		if (!state.isEdit || !column.accessor) return;
		onRowDataValueChange?.({
			value: state.value,
			originalValue: state.originalValue,
			columnId: column.id,
			accesor: column.accessor,
			rowId: rowId,
			rowData: rowData,
		});
		state.isEdit = false; // this works as a useRef or static state to prevent reSave
		setState((x) => ({ ...x, isEdit: false }));
	};
	const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Enter") {
			handleSave();
			onAdvance?.({ rowId, columnId: column.id });
		} else if (e.key === "Escape") {
			state.isEdit = false;
			setState((x) => ({ ...x, isEdit: false, value: x.editStartValue }));
		}
	};

	return (
		<td
			ref={tdRef}
			data-col-id={column.id}
			className={clx({
				[styles.cell]: true,
				[styles[`${theme}Theme`]]: true,
				[styles.editing]: state.isEdit,
				[styles.modified]: isModified,
				[styles.isEditable]: isEditable,
				[styles.isSticky]: !!column.isSticky,
				isSticky: true,
				[styles.overThreshold]: isOverThreshold,
			})}
			style={{
				width: column.width,
				minWidth: column?.minWidth,
				...(column.fullWidth ? { width: "100%" } : {}),
				...(column.getCellStyle?.({ column, rowData, value }) || {}),
				border: disableBorder ? "none" : "auto",
			}}>
			{!isSaving && isEditable && !isModified && !state.isEdit && (
				<button
					className={clx({ [styles.editBtn]: true, [column.editBtnClassName ?? ""]: !!column.editBtnClassName })}
					onClick={(e) => {
						if (column.onEditClick) {
							column.onEditClick({
								rowData,
								value: state.value,
								column: column,
							});
							return;
						}
						if (!isEditable) return;
						e.stopPropagation();
						enterEditMode();
					}}>
					{column.editableLabel ?? "Edit"}
				</button>
			)}
			{state.isEdit ? (
				<Input
					step={1}
					disableXbutton
					ref={inputRef}
					type="number"
					className={styles.input}
					wrapperClassName={styles.inputWrapper}
					inputContainerClassName={styles.inputContainer}
					value={state.value}
					controlSize={column.editInputSize ?? "sm"}
					onChange={(e) => {
						setState((x) => ({ ...x, value: e.target.value }));
					}}
					onBlur={() => {
						handleSave();
					}}
					onKeyDown={handleKeyDown}
				/>
			) : (
				content
			)}
		</td>
	);
};
